import { InfoIcon } from "@phosphor-icons/react/dist/ssr";
import { Loader } from "apps/visits/packages/ui/src/components";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { useGetPackages } from "../../api/get-packages";
import { Search } from "../../components/search";
import { CatalogList } from "../../components/v2/catalog-list";
import { SideCart } from "../../components/v2/side-cart";
import { useCatalogSearch } from "../../hooks/use-catalog-search";
import { useFlowPath } from "../../hooks/use-flow-path";
import type { BloodTest } from "../../models/blood-test";
import type { BloodTestPackage } from "../../models/blood-test-package";
import { useCartState } from "../../store/use-cart-state";
import { useSearchState } from "../../store/use-search-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { beginCheckoutEvent, safeTrackEvent } from "../../tracking/events";
import { configName } from "../config/config-name";
import { routes } from "../config/routes";

export const Catalog = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const flowPath = useFlowPath();
  const { searchTerm, searchResults, clearSearch, isSearching } = useSearchState<BloodTest>();
  const { products, productsLimit, addProduct, removeProduct, clearCart } = useCartState();
  const { voucherDistribution } = useVouchersDistributionState();

  const getPackagesQuery = useGetPackages({
    slug: voucherDistribution?.slug || "",
    configName,
  });

  const { search, clearSearchResults } = useCatalogSearch({
    products: getPackagesQuery.data?.bloodTestPackages || [],
  });

  const goToNextStep = () => {
    clearSearch();
    if (products.length > 0) {
      const analyticsProducts = products.map((p) => ({
        ...(p as unknown as BloodTestPackage),
        productType: "blood-test-package" as const,
        quantity: 1,
      }));
      const totalPrice = products.reduce((acc, p) => acc + Number(p.grossPrice), 0);
      safeTrackEvent(
        () => beginCheckoutEvent(analyticsProducts, { value: totalPrice }, { numberOfProducts: products.length }),
        "begin_checkout",
        "catalog continue",
      );
    }
    history.push(flowPath(routes.checkout.raw()));
  };

  const filteredPackages = useMemo(() => {
    if (searchTerm.length >= 2) {
      const searchResultUuids = new Set(searchResults.map((r) => r.uuid));
      return getPackagesQuery.data?.bloodTestPackages?.filter((p) => searchResultUuids.has(p.uuid));
    }
    return getPackagesQuery.data?.bloodTestPackages;
  }, [searchTerm, searchResults, getPackagesQuery.data?.bloodTestPackages]);

  const productQuantities = useMemo(() => {
    return products.reduce(
      (acc, product) => {
        acc[product.uuid] = product.quantity || 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [products]);

  if (!voucherDistribution) return null;

  const handleRemoveProduct = (product: BloodTestPackage) => {
    const cartProduct = products.find((p) => p.uuid === product.uuid);
    if (cartProduct) {
      removeProduct(cartProduct as unknown as BloodTestPackage);
    }
  };

  return (
    <IntersectionFadeIn className={cn(["mx-auto w-full max-w-[1376px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12"])}>
      <div className="">
        <div className={cn("mb-6 flex flex-col items-start")}>
          <h1 className="font-serif text-2xl lg:text-4xl">{t(`cmsTranslations.friends_and_family.catalog.title`)}</h1>
          <p className="mt-2 max-w-3xl text-sm lg:mt-4 lg:text-lg">
            {t(`cmsTranslations.friends_and_family.catalog.description`)}
          </p>
        </div>
        <SidebarLayout>
          <IntersectionFadeIn className="flex w-full flex-col gap-8">
            <div className="flex items-start gap-3 rounded-xl bg-blue-50 p-4 text-base text-blue-800 leading-relaxed">
              <InfoIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-blue-600" />
              <p>{t("VOUCHERS_DISTRIBUTION.FRIENDS_AND_FAMILY_CATALOG_NOTICE")}</p>
            </div>
            {(getPackagesQuery.data?.bloodTestPackages?.length || 0) > 7 && (
              <div className="lg:mb-2">
                <Search search={search} clearSearchResults={clearSearchResults} />
              </div>
            )}
            {getPackagesQuery.isLoading || isSearching ? (
              <div className="inset-0 z-50 flex h-[calc(100vh-24rem)] w-full items-center justify-center">
                <Loader />
              </div>
            ) : (
              filteredPackages &&
              (filteredPackages.length > 0 ? (
                <CatalogList
                  simplified={(filteredPackages?.length || 0) > 7}
                  vouchers={filteredPackages}
                  allowMultiple={true}
                  selectedProductUuids={products.map((p) => p.uuid)}
                  onAddProduct={addProduct}
                  onRemoveProduct={handleRemoveProduct}
                  onClearCart={clearCart}
                  productQuantities={productQuantities}
                  itemListId={searchTerm.length >= 2 ? "wyniki_wyszukiwania" : "listing"}
                />
              ) : (
                <div className="flex h-[calc(100vh-24rem)] flex-col items-center justify-center py-12 text-center">
                  <p className="mb-4 font-medium text-lg">{t("VOUCHERS_DISTRIBUTION.SEARCH.NO_RESULTS")}</p>
                  <Button onClick={clearSearchResults}>{t("VOUCHERS_DISTRIBUTION.SEARCH.CLEAR")}</Button>
                </div>
              ))
            )}
          </IntersectionFadeIn>
          <SideCart ctaText={t("VOUCHERS_DISTRIBUTION.CONTINUE")} onNextStep={goToNextStep} />
        </SidebarLayout>
      </div>
    </IntersectionFadeIn>
  );
};
